import React, {memo, useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {Table, type TableProps, type RowProps} from '../Table/Table.tsx'
import styles from './StickyRowTable.module.scss'

export interface StickyRow<TItem> {
    index: number
    data: TItem
}

export interface StickyRowTableProps<TItem> extends Omit<TableProps<TItem>, 'getRowProps' | 'headRef'> {
    stickyRow?: StickyRow<TItem>
}

function StickyRowTableComponent<TItem>(props: StickyRowTableProps<TItem>) {
    const {data, stickyRow, ...rest} = props
    const headRef = useRef<HTMLTableSectionElement>(null)
    const [headerHeight, setHeaderHeight] = useState(0)

    useLayoutEffect(() => {
        if(headRef.current) setHeaderHeight(headRef.current.getBoundingClientRect().height)
    }, [])

    const hasStickyRow = stickyRow != undefined
    const stickyRowInData = hasStickyRow && stickyRow.index < data.length
    const shouldAppendStickyRowToBottom = hasStickyRow && !stickyRowInData

    const tableData = useMemo(() => {
        if(shouldAppendStickyRowToBottom) return [...data, stickyRow.data]
        return data
    }, [data, stickyRow, shouldAppendStickyRowToBottom])

    const stickyIndex = hasStickyRow
        ? (stickyRowInData ? stickyRow.index : tableData.length - 1)
        : -1

    const getRowProps = useCallback((_row: TItem, index: number): RowProps | undefined => {
        if(index != stickyIndex) return undefined
        return {
            className: styles.stickyRow,
            style: {top: headerHeight, bottom: 0},
        }
    }, [stickyIndex, headerHeight])

    return (
        <Table {...rest} data={tableData} getRowProps={getRowProps} headRef={headRef} />
    )
}

export const StickyRowTable = memo(StickyRowTableComponent) as typeof StickyRowTableComponent
